import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import {
  AxisDate,
  AxisDateTimeFull,
  AxisDateWithDay,
  AxisHoursWork,
  AxisTime,
} from "../util/AxisDateTime";
import { UserLink } from "../util/Links";
import { arrayBufferToBase64Pdf, downloadInvoiceEml } from "../util/InvoiceEmail";
import { ShiftTableTitle } from "./ShiftTableTitle";
import AxisSpinner from "./AxisSpinner";
import ViewPdfModal from "../modals/ViewPdfModal";

const InvoiceStatusBadge = ({ status }) => {
  if (status === "CREATED")
    return (
      <Badge bg="warning" text="dark">
        Created
      </Badge>
    );
  if (status === "READY") return <Badge bg="success">Ready</Badge>;
  if (status === "SENT") return <Badge bg="primary">Sent</Badge>;
  return <Badge bg="secondary">{status}</Badge>;
};

// Timesheet weeks end on Sunday; default invoice date is the Tuesday after (+2 days).
const defaultInvoiceDateFromWeekEnding = (weekEndingStr) => {
  if (!weekEndingStr) return "";
  const [y, m, d] = weekEndingStr.substring(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  const date = new Date(Date.UTC(y, m - 1, d + 2));
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const ShiftInvoiceElement = ({ invoiceId }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [marking, setMarking] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [shiftReference, setShiftReference] = useState("");
  const [bankDetailsList, setBankDetailsList] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Editable fields
  const [accountRef, setAccountRef] = useState("");
  const [bookingRef, setBookingRef] = useState("");
  const [gradeText, setGradeText] = useState("");
  const [costCentreIdentifier, setCostCentreIdentifier] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [selectedBankDetailsId, setSelectedBankDetailsId] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [weekCommencing, setWeekCommencing] = useState("");

  useEffect(() => {
    if (invoiceId == null) return;
    fetchInvoice();
    fetchBankDetails();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await authAxios().get(
        `/api/backoffice/invoice/${invoiceId}`,
      );
      const data = response.data.invoice;
      setInvoice(data);
      setShiftReference(response.data.requestId || "");
      setAccountRef(data.accountRef || "");
      setBookingRef(data.bookingRef || "");
      const gradeType = response.data.gradeType;
      const defaultGradeText =
        gradeType === "HCA2" ? "200A0204" :
        gradeType === "HCA3" ? "200A0304" :
        "200A0504";
      setGradeText(data.gradeText || defaultGradeText);
      setCostCentreIdentifier(data.costCentreIdentifier || "");
      setLineItems(data.lineItems || []);
      setSelectedBankDetailsId(data.bankDetails?.id || null);
      setInvoiceDate(
        data.invoiceDate
          ? data.invoiceDate.substring(0, 10)
          : defaultInvoiceDateFromWeekEnding(data.timesheet?.weekEnding),
      );
      setWeekCommencing(
        data.weekCommencing ? data.weekCommencing.substring(0, 10) : "",
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await authAxios().get(
        "/api/backoffice/invoice/bank_details",
      );
      setBankDetailsList(response.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const validate = () => {
    const errors = [];
    if (!gradeText?.trim()) errors.push("Grade text is required.");
    if (!costCentreIdentifier?.trim()) errors.push("Cost centre is required.");
    if (lineItems.length === 0)
      errors.push("At least one line item is required.");
    lineItems.forEach((li, idx) => {
      const n = idx + 1;
      if (!li.timeRange?.trim())
        errors.push(`Line item ${n}: time range is required.`);
      if (!li.hours || li.hours <= 0)
        errors.push(`Line item ${n}: hours must be greater than zero.`);
      const rate = parseFloat(li.rate);
      if ((!li.rate && li.rate !== 0) || isNaN(rate) || rate <= 0)
        errors.push(`Line item ${n}: rate must be a number greater than zero.`);
    });
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const body = {
        accountRef,
        bookingRef,
        invoiceDate: invoiceDate || null,
        weekCommencing: weekCommencing || null,
        gradeText,
        costCentreIdentifier,
        wardId: invoice?.wardId,
        bankDetailsId: selectedBankDetailsId,
        lineItems: lineItems.map((li, idx) => ({
          id: li.id,
          timeRange: li.timeRange,
          hours: li.hours,
          rate: li.rate,
          position: idx,
        })),
      };
      const response = await authAxios().put(
        `/api/backoffice/invoice/${invoiceId}`,
        body,
      );
      const saved = response.data.invoice;
      setInvoice(saved);
      setLineItems(saved.lineItems || []);
      setInvoiceDate(
        saved.invoiceDate ? saved.invoiceDate.substring(0, 10) : "",
      );
      setWeekCommencing(
        saved.weekCommencing ? saved.weekCommencing.substring(0, 10) : "",
      );
      return saved;
    } catch (error) {
      console.log(error);
      alert("Failed to save invoice");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    try {
      setGenerating(true);
      const response = await authAxios().post(
        `/api/backoffice/invoice/${invoiceId}/generate`,
      );
      setInvoice(response.data.invoice);
      setLineItems(response.data.invoice.lineItems || []);
    } catch (error) {
      console.log(error);
      alert("Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  const saveAndGenerate = async () => {
    if (!validate()) return;
    try {
      setGenerating(true);
      setSaving(true);
      const body = {
        accountRef,
        bookingRef,
        invoiceDate: invoiceDate || null,
        weekCommencing: weekCommencing || null,
        gradeText,
        costCentreIdentifier,
        wardId: invoice?.wardId,
        bankDetailsId: selectedBankDetailsId,
        lineItems: lineItems.map((li, idx) => ({
          id: li.id,
          timeRange: li.timeRange,
          hours: li.hours,
          rate: li.rate,
          position: idx,
        })),
      };
      await authAxios().put(`/api/backoffice/invoice/${invoiceId}`, body);
      const genResponse = await authAxios().post(
        `/api/backoffice/invoice/${invoiceId}/generate`,
      );
      const generated = genResponse.data.invoice;
      setInvoice(generated);
      setLineItems(generated.lineItems || []);
      setInvoiceDate(
        generated.invoiceDate ? generated.invoiceDate.substring(0, 10) : "",
      );
      setWeekCommencing(
        generated.weekCommencing
          ? generated.weekCommencing.substring(0, 10)
          : "",
      );
    } catch (error) {
      console.log(error);
      alert("Failed to save and generate invoice");
    } finally {
      setSaving(false);
      setGenerating(false);
    }
  };

  const markSent = async () => {
    try {
      setMarking(true);
      const response = await authAxios().post(
        `/api/backoffice/invoice/${invoiceId}/mark_sent`,
      );
      setInvoice(response.data.invoice);
    } catch (error) {
      console.log(error);
      alert("Failed to mark invoice as sent");
    } finally {
      setMarking(false);
    }
  };

  const viewInvoice = async () => {
    try {
      setPdfLoading(true);
      setShowPdfModal(true);
      const response = await authAxios().get(
        `/api/backoffice/invoice/${invoiceId}/file`,
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (error) {
      console.log(error);
      alert("Failed to open invoice");
      setShowPdfModal(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadInvoice = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = invoice?.invoiceNumber
      ? `${invoice.invoiceNumber}.pdf`
      : `invoice-${invoiceId}.pdf`;
    a.click();
  };

  const downloadEml = async () => {
    try {
      setEmailLoading(true);
      const response = await authAxios().get(
        `/api/backoffice/invoice/${invoiceId}/file`,
        { responseType: "arraybuffer" },
      );
      const pdfFilename = invoice?.invoiceNumber
        ? `${invoice.invoiceNumber}.pdf`
        : `invoice-${invoiceId}.pdf`;
      const subject = invoice?.invoiceNumber
        ? `Invoice ${invoice.invoiceNumber}`
        : `Invoice #${invoiceId}`;
      downloadInvoiceEml({
        to: invoice?.trustEmail || "",
        subject,
        body: `Please find attached ${subject}.`,
        attachments: [{ filename: pdfFilename, base64: arrayBufferToBase64Pdf(response.data) }],
        filename: invoice?.invoiceNumber
          ? `${invoice.invoiceNumber}.eml`
          : `invoice-${invoiceId}.eml`,
      });
    } catch (error) {
      console.log(error);
      alert("Failed to create email");
    } finally {
      setEmailLoading(false);
    }
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { timeRange: "", hours: 0, rate: 0, position: lineItems.length },
    ]);
  };

  const removeLineItem = (idx) => {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };

  const updateLineItem = (idx, field, value) => {
    setValidationErrors([]);
    setLineItems(
      lineItems.map((li, i) => (i === idx ? { ...li, [field]: value } : li)),
    );
  };

  if (invoiceId == null) return null;
  if (loading) return <AxisSpinner loading={loading} />;
  if (invoice == null) return null;

  const hasPdf = invoice.status === "READY" || invoice.status === "SENT";
  const grandTotal = lineItems.reduce(
    (sum, li) => sum + li.hours * (li.rate || 0),
    0,
  );
  const invoicePdfTitle = `Shift: ${shiftReference || ""}; Invoice: ${invoice.invoiceNumber || "not-set"}`;

  return (
    <Fragment>
      <ShiftTableTitle title={"Invoice"} />

      <Table striped hover responsive>
        <tbody>
          <tr>
            <td>Status</td>
            <td className={"font-weight-bold"}>
              <InvoiceStatusBadge status={invoice.status} />
            </td>
          </tr>
          {invoice.invoiceNumber && (
            <tr>
              <td>Invoice Number</td>
              <td className={"font-weight-bold"}>{invoice.invoiceNumber}</td>
            </tr>
          )}
          {invoice.timesheet?.start && (
            <tr>
              <td>Shift Date</td>
              <td className={"font-weight-bold"}>
                {AxisDateWithDay(invoice.timesheet.start)}
              </td>
            </tr>
          )}
          {invoice.timesheet?.weekEnding && (
            <tr>
              <td>Timesheet Week</td>
              <td className={"font-weight-bold"}>
                {AxisDate(invoice.timesheet.weekEnding)}
              </td>
            </tr>
          )}
          <tr>
            <td>Assignment</td>
            <td className={"font-weight-bold"}>{invoice.assignment || "—"}</td>
          </tr>
          <tr>
            <td>Nurse / Band</td>
            <td className={"font-weight-bold"}>
              {invoice.nurse ? UserLink(invoice.nurse) : null}
              {invoice.nurseBand?.includes(" - ") && (
                <span className="ms-1 text-muted">
                  —{" "}
                  {invoice.nurseBand.substring(
                    invoice.nurseBand.indexOf(" - ") + 3,
                  )}
                </span>
              )}
              {!invoice.nurse && !invoice.nurseBand && "—"}
            </td>
          </tr>
          {invoice.timesheet && (
            <tr>
              <td>Hours</td>
              <td className={"font-weight-bold"}>
                {AxisTime(invoice.timesheet.start)} –{" "}
                {AxisTime(invoice.timesheet.end)}{" "}
                <span className="text-muted">
                  ({AxisHoursWork(invoice.timesheet.hoursWorked)})
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <ShiftTableTitle title={"Edit Invoice Details"} />
      <Table striped hover responsive>
        <tbody>
          <tr>
            <td style={{ width: "35%" }}>Account Ref</td>
            <td className={"font-weight-bold"}>{accountRef || "—"}</td>
          </tr>
          <tr>
            <td>Booking Ref</td>
            <td className={"font-weight-bold"}>{bookingRef || "—"}</td>
          </tr>
          <tr>
            <td>Invoice Date</td>
            <td>
              <Form.Control
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                size="sm"
              />
            </td>
          </tr>
          <tr>
            <td>Week Commencing</td>
            <td>
              <Form.Control
                type="date"
                value={weekCommencing}
                onChange={(e) => setWeekCommencing(e.target.value)}
                size="sm"
              />
            </td>
          </tr>
          <tr>
            <td>Grade Text</td>
            <td>
              <Form.Control
                value={gradeText}
                onChange={(e) => setGradeText(e.target.value)}
                size="sm"
                placeholder="e.g. Band 5 RN"
              />
            </td>
          </tr>
          <tr>
            <td>Cost Centre</td>
            <td>
              <Form.Control
                value={costCentreIdentifier}
                onChange={(e) => setCostCentreIdentifier(e.target.value)}
                size="sm"
                placeholder="Cost centre code"
              />
            </td>
          </tr>
          <tr>
            <td>Bank Details</td>
            <td>
              <Form.Select
                size="sm"
                value={selectedBankDetailsId || ""}
                onChange={(e) =>
                  setSelectedBankDetailsId(Number(e.target.value) || null)
                }
              >
                <option value="">Select bank details</option>
                {bankDetailsList.map((bd) => (
                  <option key={bd.id} value={bd.id}>
                    {bd.bankName} — {bd.accountName} ({bd.sortCode})
                  </option>
                ))}
              </Form.Select>
            </td>
          </tr>
        </tbody>
      </Table>

      {validationErrors.length > 0 && (
        <div className="alert alert-danger py-2 mb-2">
          <ul className="mb-0 ps-3">
            {validationErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <ShiftTableTitle title={"Line Items"} />
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>Time Range</th>
            <th>Hours</th>
            <th>Rate (£)</th>
            <th>Total (£)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => (
            <tr key={idx}>
              <td>
                <Form.Control
                  value={li.timeRange || ""}
                  onChange={(e) =>
                    updateLineItem(idx, "timeRange", e.target.value)
                  }
                  size="sm"
                />
              </td>
              <td>
                <Form.Control
                  type="number"
                  value={li.hours}
                  step="0.25"
                  min="0"
                  onChange={(e) =>
                    updateLineItem(
                      idx,
                      "hours",
                      parseFloat(e.target.value) || 0,
                    )
                  }
                  size="sm"
                  style={{ width: "80px" }}
                />
              </td>
              <td>
                <Form.Control
                  type="number"
                  value={li.rate || ""}
                  step="0.01"
                  min="0"
                  onChange={(e) =>
                    updateLineItem(idx, "rate", parseFloat(e.target.value) || 0)
                  }
                  size="sm"
                  style={{ width: "90px" }}
                />
              </td>
              <td>{((li.hours || 0) * (li.rate || 0)).toFixed(2)}</td>
              <td>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeLineItem(idx)}
                >
                  ✕
                </Button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} className="text-end fw-bold">
              Total
            </td>
            <td className="fw-bold">£{grandTotal.toFixed(2)}</td>
            <td />
          </tr>
        </tbody>
      </Table>

      <Button
        variant="outline-secondary"
        size="sm"
        onClick={addLineItem}
        className="mb-3"
      >
        + Add Row
      </Button>

      <div className={"shift-button-container mt-3 mb-3"}>
        <Container fluid className="px-0">
          <Row className="shift-button invoice-action-buttons g-2 mb-2 align-content-center justify-content-center">
            <Col xs={12} sm={6} lg={4}>
              <Button
                className={"shift-button-new-shift icon-text-wrapper w-100"}
                onClick={saveAndGenerate}
                disabled={generating || saving}
              >
                {generating ? "Generating…" : "Generate"}
              </Button>
            </Col>
            {hasPdf && (
              <Fragment>
                <Col xs={12} sm={6} lg={4}>
                  <Button
                    className={"shift-button-approve icon-text-wrapper w-100"}
                    onClick={viewInvoice}
                    disabled={pdfLoading}
                  >
                    {pdfLoading ? "Loading…" : "View Invoice"}
                  </Button>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Button
                    className={"shift-button-approve icon-text-wrapper w-100"}
                    onClick={downloadEml}
                    disabled={emailLoading}
                  >
                    {emailLoading ? "Preparing…" : "Download Email"}
                  </Button>
                </Col>
              </Fragment>
            )}
            {invoice.status === "READY" && (
              <Col xs={12} sm={6} lg={4}>
                <Button
                  className={"shift-button-new-shift icon-text-wrapper w-100"}
                  onClick={markSent}
                  disabled={marking}
                >
                  {marking ? "Saving…" : "Mark as Sent"}
                </Button>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      <ViewPdfModal
        visible={showPdfModal}
        close={() => setShowPdfModal(false)}
        pdfUrl={pdfUrl}
        title={invoicePdfTitle}
        onDownload={downloadInvoice}
        loading={pdfLoading}
      />

      {invoice.statusTrail && invoice.statusTrail.length > 0 && (
        <Fragment>
          <ShiftTableTitle title={"Invoice Status Trail"} />
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>Event</th>
                <th>By</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {invoice.statusTrail.map((entry) => (
                <tr key={entry.id}>
                  <td className="text-capitalize">
                    {entry.event.replace(/_/g, " ").toLowerCase()}
                  </td>
                  <td>{entry.user ? UserLink(entry.user) : "System"}</td>
                  <td>{AxisDateTimeFull(entry.created)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Fragment>
      )}
    </Fragment>
  );
};

export default ShiftInvoiceElement;
